import {
  calculateMonthlyPrice,
  removeTrailingZeros,
} from '@forest/paywall-core'
import { cn } from '@forest/shared'
import { CheckedIcon } from '~/components/icons/checked'
import { UncheckedIcon } from '~/components/icons/unchecked'
import { m } from '~/paraglide/messages'
import { platform } from '~/params'
import type { Product } from '@forest/native-services'

interface PlanOptionProps {
  title: string
  earlyBirdProduct: Product
  originalProduct: Product
  selected: boolean
  plan: 'annual' | 'monthly'
  selectPlan: (plan: PlanOptionProps['plan']) => void
  hideTag?: boolean
}

export function PlanOption({
  title,
  earlyBirdProduct,
  originalProduct,
  selected,
  plan,
  selectPlan,
  hideTag,
}: PlanOptionProps) {
  const discountPercentage = Math.round(
    ((originalProduct.price_value - earlyBirdProduct.price_value) /
      originalProduct.price_value) *
      100,
  )

  const monthlyBreakdown = m.subscription_price_month({
    monthly_price:
      plan === 'annual'
        ? calculateMonthlyPrice(earlyBirdProduct)
        : removeTrailingZeros(earlyBirdProduct.price_localized),
  })

  return (
    <button
      onClick={() => {
        selectPlan(plan)
      }}
      className={cn(
        'relative flex w-full items-center gap-4 overflow-hidden rounded-2xl border-4 border-transparent px-4 pb-3 pt-4',
        !selected && 'border-0',
      )}
      style={{
        background: selected
          ? 'linear-gradient(114deg, #EAFEBD 0%, #B2FCFF 100%) padding-box, linear-gradient(90deg, #81D200 0%, #0EBC73 47.5%, #08C1F3 87.5%) border-box'
          : 'linear-gradient(0deg, rgba(255, 255, 255, 0.50) 0%, rgba(255, 255, 255, 0.50) 100%) border-box, linear-gradient(114deg, #EAFEBD 1.4%, #B2FCFF 96.26%) border-box',
      }}
    >
      {selected ? (
        <CheckedIcon className="size-6" />
      ) : (
        <UncheckedIcon className="size-6" />
      )}
      <div className="flex shrink-0 flex-col items-start gap-1 text-left">
        <p className="font-semibold text-[#035462]">{title}</p>
        <div className="flex w-full items-center gap-1">
          <p className="text-lg font-semibold text-[#333]">
            {platform === 'china_android'
              ? m.subscription_price_early_bird({
                  price: removeTrailingZeros(earlyBirdProduct.price_localized),
                })
              : removeTrailingZeros(earlyBirdProduct.price_localized)}
          </p>
          <p className="text-sm text-[#333333] line-through opacity-50">
            {removeTrailingZeros(originalProduct.price_localized)}
          </p>
          <p className="h-[22px] rounded-[20px] bg-[#063639] px-2 text-sm text-[#E1FAF1]">
            -{discountPercentage}%
          </p>
        </div>
        {monthlyBreakdown && (
          <p className="w-full font-semibold text-[#666666]">
            {monthlyBreakdown}
          </p>
        )}
      </div>
      {!hideTag && earlyBirdProduct.eligible_trial_days > 0 && (
        <div
          className={cn(
            'absolute right-0 top-0 flex h-7 items-center rounded-bl-[16px] bg-[#08c1f3] px-3 text-sm font-medium text-[#063639]',
          )}
        >
          {m.tag_trial_day({
            free_trial_day: earlyBirdProduct.eligible_trial_days,
          })}
        </div>
      )}
    </button>
  )
}
